import React from 'react';

/**
 * ScrollPageIndicator - Shows current page number during scroll
 * 
 * Displays a floating tooltip next to the scrollbar showing "Page X of Y"
 * when the user is actively scrolling. Fades out after scrolling stops.
 * 
 * Similar to MS Word's page indicator behavior.
 */
interface ScrollPageIndicatorProps {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Whether the user is currently scrolling */
  isScrolling: boolean;
  /** Vertical position as percentage (0-100) for positioning near scrollbar thumb */
  verticalPosition: number;
}

export const ScrollPageIndicator: React.FC<ScrollPageIndicatorProps> = ({
  currentPage,
  totalPages,
  isScrolling,
  verticalPosition
}) => {
  // Don't render if not scrolling or invalid data
  if (!isScrolling || totalPages <= 0) {
    return null;
  }

  // Clamp vertical position to keep indicator visible
  const clampedPosition = Math.max(5, Math.min(95, verticalPosition));

  return (
    <div
      style={{
        position: 'fixed',
        right: '28px', // Position next to scrollbar
        top: `${clampedPosition}%`,
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(30, 30, 30, 0.9)',
        color: 'white',
        padding: '8px 14px',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: 500,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        zIndex: 99999,
        pointerEvents: 'none',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.4)',
        whiteSpace: 'nowrap',
        opacity: isScrolling ? 1 : 0,
        transition: 'opacity 0.2s ease-out',
        // Subtle border for definition
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <span style={{ opacity: 0.7 }}>Page </span>
      <span style={{ fontWeight: 600 }}>{currentPage}</span>
      <span style={{ opacity: 0.7 }}> of </span>
      <span style={{ fontWeight: 600 }}>{totalPages}</span>
    </div>
  );
};

export default ScrollPageIndicator;
